/**
 * Toucan People — the shell: the header, the hash-routed page below it, and
 * the few things that belong to no page (Escape, the brand menu's Toucan
 * items, the sync badge). Each page owns everything else about itself.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import {
  required,
  showNotice,
  styled,
  ToucanAppMenu,
  ToucanBareButton,
  ToucanHeaderAction,
  ToucanPageHeading,
  ToucanSettings,
  useDoneKey,
  useRoute,
  useStore,
} from "toucan";
import { FormPage } from "./pages/FormPage.tsx";
import { ImportPage } from "./pages/ImportPage.tsx";
import { ListPage } from "./pages/ListPage.tsx";
import { QrSettings } from "./components/QrSettings.tsx";
import { ReviewPage } from "./pages/ReviewPage.tsx";
import { TagsPage } from "./pages/TagsPage.tsx";
import {
  currentRoute,
  go,
  isListRoute,
  listPath,
  listTagId,
  noteListRoute,
  personById,
  reviewPath,
  reviewTagId,
  routePersonId,
  tagPath,
} from "./routes.ts";
import {
  rememberPersonSort,
  reviewScopeIds,
  reviewSummaryCounts,
  storedPersonSort,
  tagById,
} from "./model.ts";
import { sessionKind } from "./session.ts";
import { pageTitle, reviewName } from "./title.ts";
import { app, store } from "./state.ts";
import type { JSX } from "preact";
import type { Person, PersonSort, ReviewCounts } from "./model.ts";
import type { Route } from "./routes.ts";

/** What the header names the page currently showing. */
const TITLES: Record<Route["name"], string> = {
  list: "",
  tag: "",
  add: "Add a person",
  edit: "Edit person",
  review: "Learn",
  tags: "Manage tags",
  import: "Import people",
  settings: "Settings",
};

/** An open book with print on its pages, as a mask the button's text colour
 * fills. */
const BOOK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 6.5C10 5 7 4.5 3 5v13c4-.5 7 0 9 1.5 2-1.5 5-2 9-1.5V5c-4-.5-7 0-9 1.5ZM12 6.5V19.5'/%3E%3Cpath d='M6 9h3M6 12h3M15 9h3M15 12h3' stroke-width='1.6'/%3E%3C/svg%3E")`;

/** The route to show, which is the list while a stale one is redirecting. */
function shownRoute(route: Route, stale: boolean): Route {
  return stale ? { name: "list" } : route;
}

/** The brand row, with the app's icon and name and the dropdown menu. */
function BrandHeader() {
  return (
    <ToucanAppMenu app={app()} label="People" icon="icon.svg">
      <li>
        <button
          type="button"
          onClick={() => {
            go("tags");
          }}
        >
          Manage tags
        </button>
      </li>
      <li>
        <button
          type="button"
          onClick={() => {
            go("import");
          }}
        >
          Import people
        </button>
      </li>
    </ToucanAppMenu>
  );
}

/* The quiz and its score sit together at the end of the brand row. The wrapper
   is sized to its contents, so the pill's own `margin-left: auto` has no free
   space to take and the gap is what parts the two. */
const ReviewCorner = styled("div")`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;

  .review-score {
    text-align: right;
    color: var(--toucan-muted);
  }

  /* A phone has room for the score and the pill, and not for the word that
     says what the score is of — which is what a tap on it spells out. */
  .score-of {
    display: none;
  }

  @media (min-width: 600px) {
    .score-of {
      display: inline;
    }
  }

  .review-action {
    display: inline-flex;
    align-items: center;
    gap: 0.35em;
  }

  .review-icon {
    flex: none;
    width: 1.25em;
    height: 1.25em;
    background: currentColor;
    -webkit-mask: center / contain no-repeat ${BOOK};
    mask: center / contain no-repeat ${BOOK};
  }

  /* An iPhone's bar has no room for the word beside the +, so the pill is a
     round glass button with the book alone, as iOS draws a bar button. */
  @media (max-width: 719px) {
    :root[data-platform="ios"] & .review-action {
      justify-content: center;
      width: 44px;
      padding: 0;
    }

    :root[data-platform="ios"] & .review-icon {
      width: 24px;
      height: 24px;
    }

    :root[data-platform="ios"] & .review-word {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
  }
`;

/**
 * How many of the people in view are known. Only the number fits beside the
 * pill on a phone, so a tap spells out the full breakdown.
 */
function ReviewScore({ counts }: { counts: ReviewCounts }) {
  // Labelled with the word the card's meter uses.
  const words = `Review progress: ${String(counts.known)} known · ${String(counts.learning)} learning · ${String(counts.new)} new`;
  return (
    <ToucanBareButton
      class="review-score"
      label={words}
      onClick={() => {
        showNotice(words);
      }}
    >
      {counts.known}/{counts.total}
      <span class="score-of"> known</span>
    </ToucanBareButton>
  );
}

/**
 * The list header's one action, which opens the quiz and shows how far through
 * the filtered people it has got. Quiet chrome in the corner rather than a
 * filled button in the toolbar, since the list itself is what the page is for.
 */
function ReviewAction({ activeTagIds }: { activeTagIds: string[] }) {
  const scope = reviewScopeIds(store(), activeTagIds);
  const counts = reviewSummaryCounts(store(), scope);
  // Nobody to ask yet is somebody still to learn.
  const kind = counts.total === 0 ? "learn" : sessionKind(store(), scope);
  return (
    <ReviewCorner>
      {counts.total > 0 && <ReviewScore counts={counts} />}
      <ToucanHeaderAction
        class="review-action"
        disabled={counts.total === 0}
        onClick={() => {
          go(reviewPath(kind, activeTagIds[0]));
        }}
      >
        <span class="review-icon" aria-hidden="true" />
        <span class="review-word">{kind === "learn" ? "Learn" : "Revise"}</span>
      </ToucanHeaderAction>
    </ReviewCorner>
  );
}

/**
 * The slim sub-page bar, with a back arrow and the page's name.
 *
 * No Save: every page under it writes as it is filled in, so leaving by the
 * arrow is all there is to do ([User experience](../../docs/user-experience.md)).
 */
function PageHeader({ route }: { route: Route }) {
  return (
    <ToucanPageHeading
      onBack={() => {
        go(listPath());
      }}
    >
      {route.name === "review" ? reviewName(route) : TITLES[route.name]}
    </ToucanPageHeading>
  );
}

/**
 * The state that outlives the page reading it.
 *
 * The tag filtering the list prefills a new person's tags on the capture
 * form, and sets the review page's scope. The list's order is kept too. The
 * quiz shows its shortcut hints while a keyboard is in use.
 */
interface Shared {
  activeTagIds: string[];
  onFilter: (tagId: string | undefined) => void;
  sort: PersonSort;
  onSort: (sort: PersonSort) => void;
  query: string;
  onQuery: (query: string) => void;
  hints: boolean;
  onHints: () => void;
}

/** The page for a route, given the person it is about where it names one. */
function Page({
  route,
  person,
  shared,
}: {
  route: Route;
  person: Person | undefined;
  shared: Shared;
}) {
  const {
    activeTagIds,
    onFilter,
    sort,
    onSort,
    query,
    onQuery,
    hints,
    onHints,
  } = shared;
  const list = (): JSX.Element => (
    <ListPage
      activeTagIds={activeTagIds}
      onFilter={onFilter}
      sort={sort}
      onSort={onSort}
      query={query}
      onQuery={onQuery}
    />
  );
  const pages: Record<Route["name"], () => JSX.Element> = {
    list,
    tag: list,
    add: () => <FormPage key="add" person={null} activeTagIds={activeTagIds} />,
    edit: () => {
      const editing = required(person, "person");
      return (
        <FormPage
          key={editing.id}
          person={editing}
          activeTagIds={activeTagIds}
        />
      );
    },
    review: () => {
      // The session's scope is in its address, not the list it was opened from.
      const tagId = reviewTagId(route);
      // This is the review route's own page, so the route is that one.
      const { kind } = route as Extract<Route, { name: "review" }>;
      return (
        <ReviewPage
          // A new kind is a new session, from nothing.
          key={kind}
          kind={kind}
          tagId={tagId}
          activeTagIds={tagId === undefined ? [] : [tagId]}
          hints={hints}
          onHints={onHints}
        />
      );
    },
    tags: () => <TagsPage />,
    import: () => <ImportPage activeTagIds={activeTagIds} />,
    settings: () => (
      <ToucanSettings app={app()}>
        <QrSettings />
      </ToucanSettings>
    ),
  };
  return pages[route.name]();
}

export function Shell() {
  const route = useRoute(currentRoute);
  const [hints, setHints] = useState(false);
  // Which way this browser likes its list: the one last chosen here, or Name.
  const [sort, setSort] = useState<PersonSort>(storedPersonSort);
  // The list's search is kept up here, since the list unmounts while a person
  // is edited and the search should survive that.
  const [query, setQuery] = useState("");
  useStore(app());
  const personId = routePersonId(route);
  const person = personId === undefined ? undefined : personById(personId);
  // An edit page whose person has gone (deleted here, or synced away
  // on another device) drops back to the list rather than rendering a hole.
  const stale = personId !== undefined && person === undefined;
  useEffect(() => {
    if (stale) {
      go(listPath());
    }
  }, [stale]);
  // A tag that has gone — a stale link, or a tag another device deleted — is
  // everybody, under the list's own address rather than one nothing answers
  // to.
  const staleTag =
    route.name === "tag" && tagById(store().tags, route.id) === undefined;
  useEffect(() => {
    if (staleTag) {
      go("");
    }
  }, [staleTag]);
  const shown = shownRoute(route, stale || staleTag);
  // Record the route itself, not the everybody stand-in `shown` uses while a
  // deleted person's page redirects, which would forget the tag. A deleted
  // tag is not recorded.
  if (!staleTag) {
    noteListRoute(route);
  }
  // The tag the list is narrowed to, and so the one a new person is filed
  // under and the quiz is scoped to. One that has gone — deleted here, or
  // synced away — narrows nothing.
  const tagId = listTagId();
  const activeTagIds =
    tagId !== undefined && tagById(store().tags, tagId) !== undefined
      ? [tagId]
      : [];

  // Every page starts at the top, however far the last one had scrolled.
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    required(main.current, "main").scrollTop = 0;
  }, [route]);

  // Names the tab. Run on every render rather than on the route,
  // since a title can be made of library data — a person's name — and renaming
  // one has to reach the tab as well as the page.
  useEffect(() => {
    document.title = pageTitle(shown);
  });

  // ⌘/Ctrl+Enter presses the page's one primary action from inside the field
  // being typed in — the import's Find photos, then its Add — and on a page
  // with none, the edit form, leaves it. A ghosted action is one the page
  // cannot take yet, and the key waits with it rather than leaving.
  useDoneKey(!isListRoute(shown), () => {
    const primary = document.querySelector<HTMLButtonElement>(
      'main button[data-variant="primary"]',
    );
    if (primary === null) {
      go(listPath());
    } else if (!primary.disabled) {
      primary.click();
    }
  });

  // Escape leaves a sub-page, the same as tapping the back arrow — unless a
  // dialog is open, which already handles Escape itself.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (
        event.key === "Escape" &&
        !isListRoute(shownRoute(route, stale)) &&
        document.querySelector("dialog[open]") === null
      ) {
        event.preventDefault();
        go(listPath());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [route, stale]);

  return (
    <>
      <header>
        {isListRoute(shown) ? (
          <>
            <BrandHeader />
            <ReviewAction activeTagIds={activeTagIds} />
          </>
        ) : (
          <PageHeader route={shown} />
        )}
      </header>
      <main ref={main}>
        <Page
          route={shown}
          person={person}
          shared={{
            activeTagIds,
            // A tag is a place in the list, so it is a hash. The back gesture
            // undoes it and the tab is named after it, and a link can hold it.
            onFilter: (picked) => {
              go(tagPath(picked));
            },
            sort,
            onSort: (next) => {
              setSort(next);
              rememberPersonSort(next);
            },
            query,
            onQuery: setQuery,
            hints,
            onHints: () => {
              setHints(true);
            },
          }}
        />
      </main>
    </>
  );
}
